'use client';
import React, { useState, useEffect } from 'react';
import { Button, Input, GlobalLoading, Breadcrumb } from '@/shared/components';
import AdvancedSearch from './advanced-search';
import { OrderHistoryTable } from './order-history-table';
import ExportOrders from './export-orders';
import RangeSelector from './range-selector';
import { useQueryOrderHistoryList } from '@/hooks/orderhistory.hook';
import { number } from 'mathjs';
import { isArray } from 'lodash';
import { usePageTracking } from '@/shared/tracking/use-tracking';
import { LINKS } from '@/app/utils/constant';
import clsx from 'clsx';
import { scrollToTop, trimAll } from '@/app/utils';
import { NAMESPACE, useLang } from '@/hooks/use-lang';

export default function OrderHistory() {
  usePageTracking();
  const { t } = useLang(NAMESPACE.orderHistory);
  const { t: commonT } = useLang(NAMESPACE.common);
  const [search, setSearch] = useState('');
  const [showAdvancedSearch, setAdvancedSearch] = useState(false);
  const [pageSize, setPageSize] = useState<number>(15);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [sorting, setSorting] = useState([]);
  const [columnFilters, setColumnFilters] = useState([]);
  const [sortingParam, setSortingParam] = useState<Object>({ order_date: 'desc' });
  const [isRangeSelector, setRangeSelector] = useState<string>('range');
  const [singleValue, setSingleValue] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [advanceSearchParams, setAdvancedSearchParams] = useState({});
  const [orderData, setOrderData] = useState<any>();

  const { data, isLoading } = useQueryOrderHistoryList({
    pageSize: pageSize,
    pageNumber: pageNumber,
    orderBy: sortingParam,
    advanceSearchParams,
  });

  useEffect(() => {
    scrollToTop();
  }, [data]);

  useEffect(() => {
    if (isLoading) {
      GlobalLoading.show();
    } else {
      GlobalLoading.hide();
    }
  }, [isLoading]);

  useEffect(() => {
    if (data) {
      setOrderData(data);
    }
  }, [data]);

  const updateSearch = (param) => {
    let tempVal = { ...advanceSearchParams };
    if (Object.keys(tempVal).length > 0) {
      tempVal = { ...tempVal, ...param };
    } else {
      tempVal = { ...param };
    }
    setPageNumber(number(1));
    setPageSize(number(15));
    setAdvancedSearchParams(tempVal);
  };

  const handleSingleDateSearch = () => {
    if (singleValue !== '') {
      const temp = {
        singleDate: singleValue,
        startDate: '',
        endDate: '',
      };
      updateSearch(temp);
    }
  };

  const handleRangeDateSearch = () => {
    if (endDate !== '' && startDate !== '') {
      const temp = {
        startDate: startDate,
        endDate: endDate,
        singleDate: '',
      };
      updateSearch(temp);
    }
  };

  useEffect(() => {
    if (isRangeSelector === 'range') {
      handleRangeDateSearch();
    } else if (isRangeSelector === 'single') {
      handleSingleDateSearch();
    }
  }, [isRangeSelector]);

  useEffect(() => {
    handleSingleDateSearch();
  }, [singleValue]);

  useEffect(() => {
    handleRangeDateSearch();
  }, [startDate, endDate]);

  const handleSearchEnterKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      searchboxFunc();
    }
  };

  const searchboxFunc = () => {
    const temp = {
      simpleSearchStr: search?.trim(),
    };
    updateSearch(temp);
  };

  const clearFilters = () => {
    setPageNumber(number(1));
    setPageSize(number(15));
    setAdvancedSearch(false);
    setSearch('');
    setAdvancedSearchParams({});
  };

  const onSearch = (params) => {
    const tempObj = {
      ...params,
      ...(startDate && { startDate: startDate }),
      ...(endDate && { endDate: endDate }),
      ...(singleValue && { singleDate: singleValue }),
      ...(search && { simpleSearchStr: search }),
    };
    setPageNumber(number(1));
    setPageSize(number(15));
    setAdvancedSearchParams(trimAll(tempObj));
  };

  const updatePageNumber = (e) => {
    setPageNumber(e);
  };

  const updatePageSize = (e) => {
    setPageSize(e);
  };

  useEffect(() => {
    const filtersParams = {};
    columnFilters.forEach((filterItem) => {
      filtersParams[filterItem['id']] = filterItem['value'].join(',');
    });
    const tempVal = { ...advanceSearchParams, ...filtersParams };

    if (tempVal['orderSource'] === '') {
      delete tempVal['orderSource'];
    }

    if (tempVal['orderStatus'] === '') {
      delete tempVal['orderStatus'];
    }

    setAdvancedSearchParams(tempVal);
  }, [columnFilters]);

  useEffect(() => {
    if (sorting && isArray(sorting) && sorting[0]) {
      const sortKey = {
        orderDate: 'order_date',
        orderNumber: 'e1_order_number',
        poNumber: 'purchase_order_number',
        amtTotalPrice: 'total_price',
        orderReferenceNo: 'web_order_number',
      };
      const temp = {};
      temp[sortKey[sorting[0]['id']]] = sorting[0]['desc'] ? 'desc' : 'asc';
      setSortingParam(temp);
    } else {
      setSortingParam({ order_date: 'desc' });
    }
  }, [sorting]);

  const sortFunc = (props) => {
    setSorting(props);
  };

  const filterColumnFunc = (props) => {
    setPageNumber(1);
    setColumnFilters(props);
  };
  const buttonType = showAdvancedSearch ? 'primary' : 'outline';
  const buttonClass = showAdvancedSearch
    ? 'bg-[#1e8ae7] border-[#1e8ae7] ml-auto sm:m-auto sm:flex sm:justify-center sm:w-full sm:p-0 sm:mt-2'
    : 'ml-auto sm:m-auto sm:flex sm:justify-center sm:w-full sm:p-0 sm:mt-2';
  return (
    <div className="grid grid-cols-[1fr_345px] min-h-[400px] w-full">
      <div className="col-span-2 mt-8 w-full mb-5">
        <Breadcrumb
          items={[
            { name: commonT('home'), url: '/' },
            { name: commonT('account'), url: LINKS.myAccount },
            { name: t('order.history') },
          ]}
        />
      </div>
      <div className="col-span-2 grid mt-8 w-full mb-4">
        <div className="flex items-baseline gap-x-4 w-full">
          <h1 className="font-medium">{t('order.history')}</h1>
        </div>
        <div className="flex sm:inline-flex sm:flex-col items-baseline gap-x-4 w-full lg:row-start-3 col-start-1 border-0 border-b-2 border-solid border-gray-300 pb-[12px]">
          <div>
            <div className="font-bold pb-[12px] border-0 border-b-2 border-solid border-red-500">
              {t('order.history')}
            </div>
          </div>
          <ExportOrders
            searchParams={advanceSearchParams}
            sortParams={sortingParam}
            pageSize={pageSize}
            pageNumber={pageNumber}
          />
        </div>
      </div>
      <div className="col-span-2 grid mt-8 w-full mb-8">
        <div className="flex mb-10 sm:inline-flex sm:flex-col">
          <div className="relative mr-12 md:mr-4 border border-solid border-gray-450 sm:mt-2 flex flex-1 sm:mr-0">
            <Input
              placeholder={t('search.placeholder')}
              className="min-w-[250px] c-input--medium w-full text-ellipsis border-0"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
              }}
              onKeyDown={(e) => {
                handleSearchEnterKey(e);
              }}
            />
            <Button
              className="right-0 top-0 mini box-border border-0 bg-[white] rounded-r relative"
              onClick={searchboxFunc}
            >
              <i
                className="icon-24-search-mono text-gray-500 absolute left-2/4 top-2/4"
                style={{ transform: 'translate(-50%, -50%)' }}
              ></i>
            </Button>
          </div>
          <div>
            <Button
              className={clsx(buttonClass, 'flex items-center')}
              onClick={() => {
                setAdvancedSearch(!showAdvancedSearch);
              }}
              type={buttonType}
              endIconSize="8"
              endIconName={showAdvancedSearch ? 'caret-up' : 'caret-down'}
            >
              <p>{t('advanced.search')}</p>
            </Button>
          </div>
        </div>
        {showAdvancedSearch && <AdvancedSearch onSearch={onSearch} />}
        <RangeSelector
          clearFilters={clearFilters}
          isRangeSelector={isRangeSelector}
          setRangeSelector={setRangeSelector}
          singleValue={singleValue}
          setSingleValue={setSingleValue}
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
        />
      </div>
      {orderData && (
        <div className="col-span-2 w-full flex flex-col">
          <OrderHistoryTable
            data={orderData.data}
            page={orderData.page}
            updatePageNumber={updatePageNumber}
            updatePageSize={updatePageSize}
            sortFunc={sortFunc}
            sorting={sorting}
            filterFunc={filterColumnFunc}
            columnFilters={columnFilters}
          ></OrderHistoryTable>
        </div>
      )}
    </div>
  );
}
